import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { PaymentCard, SearchView } from "../../../components";
import { getPayments } from "../../../data";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { countryOptions } from "@/utils/country-options";

export function PaymentListScreen() {
  const { isAdvancedTable } = useTableView();
  const [pageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [searchParams, setSearchParams] = useState({
    name: "",
    invoiceId: "",
    country: "",
    year: "",
    type: "",
  });

  const navigate = useNavigate();

  const [page, setPage] = useState(() => {
    const storedPage = localStorage.getItem(`paymentListPage`);
    return storedPage ? parseInt(storedPage, 10) : 1;
  });
  const [queryParams, setQueryParams] = useState(() => {
    const storedQueryParams = localStorage.getItem(`paymentListQueryParams`);
    if (storedQueryParams && storedQueryParams !== "{}") {
      setSearchParams(JSON.parse(storedQueryParams));
    }
    return storedQueryParams ? JSON.parse(storedQueryParams) : {};
  });
  useEffect(() => {
    localStorage.setItem(`paymentListPage`, page.toString());
    localStorage.setItem(`paymentListQueryParams`, JSON.stringify(queryParams));
  }, [page, queryParams]);

  const queryClient = useQueryClient();
  const searchTimeoutRef = useRef(null);
  const { data, isLoading } = useQuery({
    queryKey: ["payments", page, pageSize, queryParams, sortColumn, sortDirection],
    queryFn: () =>
      getPayments({
        page,
        pageSize,
        nameFilter: queryParams.name,
        invoiceIdFilter: queryParams.invoiceId,
        countryFilter: queryParams.country,
        yearFilter: queryParams.year,
        typeFilter: queryParams.type,
        sortColumn,
        sortDirection,
      }),
  });

  const pageSettings = {
    title: "Payment List",
    actions: [
      {
        name: "Payment Hesapla",
        color: "bg-green-500 hover:bg-green-700 text-white",
        icon: "calculator-icon",
        onClick: () => {
          navigate("/payments/calculate");
        },
      },
      {
        name: "Create New Payment",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        icon: "add-icon",
        onClick: () => {
          navigate("/payments/add");
        },
      },
    ],
  };

  const goToDetail = (id) => () => {
    navigate(`/payments/${id}`);
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  // Eski tablo için basit kolonlar
  const simpleColumns = [
    { key: "name", label: "Name" },
    { key: "invoiceId", label: "Invoice ID" },
    { key: "type", label: "Type" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div>
          <Button onClick={goToDetail(row.id)}>View Details</Button>
        </div>
      ),
    },
  ];

  // Gelişmiş tablo için tüm kolonlar
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "invoiceId", label: "Invoice ID", sortable: true },
    { key: "status", label: "Status", sortable: true },
    { 
      key: "isMailSended", 
      label: "Mail Sent", 
      sortable: true,
      accessor: (item) => item.isMailSended ? "Yes" : "No"
    },
    { key: "type", label: "Type", sortable: true },
    { 
      key: "relatedUser", 
      label: "Related User", 
      sortable: false,
      accessor: (item) => item.relatedUser || "-"
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <div>
          <Button size="sm" onClick={goToDetail(row.id)}>View Details</Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = ["name", "invoiceId", "status", "type", "actions"];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
    >
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        countryOptions={countryOptions}
        yearOptions={["2024", "2025", "2026"]}
        typeOptions={["DEFAULT", "FIXED"]}
        resetDefault={() => {
          setSearchParams({
            name: "",
            invoiceId: "",
            country: "",
            year: "",
            type: "",
          });
          setQueryParams({
            name: "",
            invoiceId: "",
            country: "",
            year: "",
            type: "",
          });
          setPage(1);
        }}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data.payments}
          columns={advancedColumns}
          columnVisibilityKey="payment-list-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.id}`)}
        />
      ) : (
        <ListView
          isLoading={isLoading}
          searchParams={searchParams}
          handleSearch={handleSearch}
          data={data?.data.payments}
          columns={simpleColumns}
          totalItems={data?.data.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.id}`)}
          renderGridItem={(item) => (
            <PaymentCard
              key={item.id}
              name={item.name}
              invoiceId={item.invoiceId}
              type={item.type}
              editClick={goToDetail(item.id)}
            />
          )}
        />
      )}
    </AppLayout>
  );
}
